import { useEffect, useMemo, useRef, useState, type KeyboardEvent } from "react";
import { Tooltip } from "@nexus/ui";

import type { ChainGraph, ChainLabels } from ".";
import { formatValue, isOtherNode, stageLabel } from ".";

// ── Model ─────────────────────────────────────────────────────────────────────

export interface FunnelStage {
  stage: string;
  label: string;
  /** Distinct entities in the stage (services, watermark IDs, accounts…). */
  count: number;
  /** True when an `Other` node did not say how many entities it merges: the count is a floor. */
  atLeast: boolean;
  /** Streams through the stage — the flow unit, the same for every stage. */
  flow: number;
}

/**
 * One entry per stage, counting the entities it holds. The flow is conserved
 * across stages, so it cannot tell stages apart; the number of distinct
 * services, watermark IDs or accounts is what shows where the chain narrows.
 */
export function summarizeStages(graph: ChainGraph, labels: ChainLabels): FunnelStage[] {
  return graph.stages
    .map((stage) => {
      const nodes = graph.nodes.filter((node) => node.stage === stage);
      return {
        stage,
        label: stageLabel(stage, labels),
        count: nodes.reduce((sum, node) => sum + Math.max(1, Math.round(node.members ?? 1)), 0),
        atLeast: nodes.some((node) => isOtherNode(node) && node.members === undefined),
        flow: nodes.reduce((sum, node) => sum + node.value, 0),
      };
    })
    .filter((stage) => stage.count > 0);
}

// ── Layout ────────────────────────────────────────────────────────────────────

export type FunnelOrientation = "horizontal" | "vertical";

export interface FunnelBlock {
  x: number;
  y: number;
  width: number;
  height: number;
  /** Where the stage label is anchored (horizontal: centre of its bottom edge; vertical: right edge, middle). */
  labelX: number;
  labelY: number;
  /** Room the label may take before it wraps (horizontal) or truncates (vertical). */
  labelWidth: number;
}

export interface FunnelLayout {
  orientation: FunnelOrientation;
  width: number;
  height: number;
  blocks: FunnelBlock[];
  /** SVG paths joining each block to the next: `ribbons[i]` joins block i and i + 1. */
  ribbons: string[];
}

/** Below this width the stages stack vertically: side by side, their names would collide. */
export const FUNNEL_VERTICAL_BELOW = 560;
const LABEL_SPACE = 44;
const MIN_BLOCK = 52;
const ROW = 44;
const ROW_GAP = 22;
const VERTICAL_LABEL_COLUMN = 0.34;

function ribbonH(a: FunnelBlock, b: FunnelBlock): string {
  const x1 = a.x + a.width;
  const x2 = b.x;
  const mid = (x1 + x2) / 2;
  return [
    `M${x1},${a.y}`,
    `C${mid},${a.y} ${mid},${b.y} ${x2},${b.y}`,
    `L${x2},${b.y + b.height}`,
    `C${mid},${b.y + b.height} ${mid},${a.y + a.height} ${x1},${a.y + a.height}`,
    "Z",
  ].join(" ");
}

function ribbonV(a: FunnelBlock, b: FunnelBlock): string {
  const y1 = a.y + a.height;
  const y2 = b.y;
  const mid = (y1 + y2) / 2;
  return [
    `M${a.x},${y1}`,
    `C${a.x},${mid} ${b.x},${mid} ${b.x},${y2}`,
    `L${b.x + b.width},${y2}`,
    `C${b.x + b.width},${mid} ${a.x + a.width},${mid} ${a.x + a.width},${y1}`,
    "Z",
  ].join(" ");
}

/**
 * Lays the stages out as blocks sized by their count, centred on one line, with
 * tapering ribbons between neighbours. Pure: sized in pixels from the container,
 * so the text stays crisp instead of scaling with a viewBox.
 */
export function layoutFunnel(stages: readonly FunnelStage[], width: number, height: number): FunnelLayout {
  const orientation: FunnelOrientation = width < FUNNEL_VERTICAL_BELOW ? "vertical" : "horizontal";
  const n = stages.length;
  const max = Math.max(1, ...stages.map((stage) => stage.count));
  if (n === 0 || width <= 0) return { orientation, width, height: 0, blocks: [], ribbons: [] };

  if (orientation === "vertical") {
    const labelColumn = Math.round(width * VERTICAL_LABEL_COLUMN);
    const track = width - labelColumn;
    const minBar = Math.min(track, 56);
    const blocks = stages.map((stage, index) => {
      const barWidth = Math.max(minBar, (stage.count / max) * track);
      const y = index * (ROW + ROW_GAP);
      return {
        x: labelColumn + (track - barWidth) / 2,
        y,
        width: barWidth,
        height: ROW,
        labelX: labelColumn - 12,
        labelY: y + ROW / 2,
        labelWidth: labelColumn - 12,
      };
    });
    const ribbons = blocks.slice(1).map((block, index) => ribbonV(blocks[index], block));
    return { orientation, width, height: n * ROW + (n - 1) * ROW_GAP, blocks, ribbons };
  }

  // Blocks about half as wide as the flow that follows them, as in a funnel read left to right.
  const blockWidth = Math.round(Math.min(150, Math.max(56, width / (n * 2.1))));
  // Stage names are centred over their block: leave room for the first and last ones.
  const textWidth = (stage: FunnelStage) => Math.min(160, stage.label.length * 6.8);
  const padLeft = Math.max(0, textWidth(stages[0]) / 2 - blockWidth / 2);
  const padRight = Math.max(0, textWidth(stages[n - 1]) / 2 - blockWidth / 2);
  const span = width - padLeft - padRight;
  const gap = n > 1 ? (span - n * blockWidth) / (n - 1) : 0;
  const pitch = blockWidth + gap;
  const maxBlock = Math.max(MIN_BLOCK, height - LABEL_SPACE - 4);
  const middle = LABEL_SPACE + maxBlock / 2;
  const labelWidth = Math.max(blockWidth, Math.min(176, pitch - 12));
  const blocks = stages.map((stage, index) => {
    const blockHeight = Math.max(MIN_BLOCK, (stage.count / max) * maxBlock);
    const x = n > 1 ? padLeft + index * pitch : (width - blockWidth) / 2;
    const y = middle - blockHeight / 2;
    return { x, y, width: blockWidth, height: blockHeight, labelX: x + blockWidth / 2, labelY: y - 8, labelWidth };
  });
  const ribbons = blocks.slice(1).map((block, index) => ribbonH(blocks[index], block));
  return { orientation, width, height, blocks, ribbons };
}

export function formatStageCount(stage: FunnelStage): string {
  const value = stage.count >= 10_000
    ? new Intl.NumberFormat("en", { notation: "compact", maximumFractionDigits: 1 }).format(stage.count)
    : formatValue(stage.count);
  return stage.atLeast ? `${value}+` : value;
}

// ── Component ─────────────────────────────────────────────────────────────────

const HORIZONTAL_HEIGHT = 300;

/**
 * One sober colour for the whole chain: a count per stage does not need a hue
 * per stage, and six categorical colours side by side read as decoration. The
 * block uses the first categorical token (never brand cyan, reserved for
 * interaction); the flows are a light tint of it.
 */
const BLOCK_FILL = "var(--chart-1)";
const FLOW_OPACITY = 0.2;
const FLOW_OPACITY_DIMMED = 0.07;

function useWidth<T extends HTMLElement>() {
  const ref = useRef<T | null>(null);
  const [width, setWidth] = useState(0);
  useEffect(() => {
    const host = ref.current;
    if (!host) return;
    setWidth(host.clientWidth);
    const observer = new ResizeObserver(([entry]) => setWidth(Math.round(entry.contentRect.width)));
    observer.observe(host);
    return () => observer.disconnect();
  }, []);
  return [ref, width] as const;
}

/**
 * The light view of the supply chain: one block per stage, as tall as the stage
 * has entities, joined by tapering flows. Reads at a glance from across a room;
 * the full per-node flow stays one setting away in the detailed view.
 */
export function StageFunnel({
  graph,
  labels,
  description,
}: {
  graph: ChainGraph;
  labels: ChainLabels;
  description: string;
}) {
  const [hostRef, width] = useWidth<HTMLDivElement>();
  const [active, setActive] = useState<number | null>(null);
  const stages = useMemo(() => summarizeStages(graph, labels), [graph, labels]);
  const layout = useMemo(() => layoutFunnel(stages, width, HORIZONTAL_HEIGHT), [stages, width]);
  const vertical = layout.orientation === "vertical";

  // A ribbon stays lit when either of its ends is the active stage.
  const lit = (index: number) => active === null || index === active;
  const ribbonLit = (index: number) => active === null || index === active || index + 1 === active;

  function onKeyDown(event: KeyboardEvent<SVGGElement>, index: number) {
    const step = event.key === "ArrowRight" || event.key === "ArrowDown" ? 1 : event.key === "ArrowLeft" || event.key === "ArrowUp" ? -1 : 0;
    if (step === 0) return;
    event.preventDefault();
    const next = (index + step + stages.length) % stages.length;
    (event.currentTarget.parentElement?.querySelectorAll<SVGGElement>("[data-funnel-block]")[next])?.focus();
  }

  return (
    <div ref={hostRef} className="relative w-full" style={{ height: vertical ? layout.height || undefined : HORIZONTAL_HEIGHT }}>
      {width > 0 && (
        <>
          <svg
            role="img"
            aria-label={description}
            width={layout.width}
            height={layout.height}
            className="absolute inset-0 overflow-visible"
          >
            {layout.ribbons.map((d, index) => (
              <path
                key={`ribbon-${stages[index].stage}`}
                d={d}
                style={{ fill: BLOCK_FILL, fillOpacity: ribbonLit(index) ? FLOW_OPACITY : FLOW_OPACITY_DIMMED }}
                className="transition-[fill-opacity] duration-200 motion-reduce:transition-none"
              />
            ))}
            {layout.blocks.map((block, index) => {
              const stage = stages[index];
              const fontSize = vertical ? 17 : Math.round(Math.min(30, Math.max(18, block.width * 0.28)));
              return (
                <Tooltip
                  key={stage.stage}
                  side={vertical ? "right" : "bottom"}
                  className="block min-w-40 px-3 py-2"
                  content={
                    <>
                      <span className="block font-semibold">{stage.label}</span>
                      <span className="mt-1 flex justify-between gap-4 font-normal text-muted">
                        <span>Count</span>
                        <span className="font-semibold tabular-nums text-primary">{formatStageCount(stage)}</span>
                      </span>
                      <span className="flex justify-between gap-4 font-normal text-muted">
                        <span className="first-letter:uppercase">{labels.flowName}</span>
                        <span className="font-semibold tabular-nums text-primary">{formatValue(stage.flow)}</span>
                      </span>
                    </>
                  }
                >
                  <g
                    data-funnel-block=""
                    role="button"
                    tabIndex={0}
                    aria-label={`${stage.label}: ${formatStageCount(stage)}, ${formatValue(stage.flow)} ${labels.flowName}`}
                    onMouseEnter={() => setActive(index)}
                    onMouseLeave={() => setActive(null)}
                    onFocus={() => setActive(index)}
                    onBlur={() => setActive(null)}
                    onKeyDown={(event) => onKeyDown(event, index)}
                    className="cursor-default outline-none [&:focus-visible>rect]:stroke-[var(--focus)] [&:focus-visible>rect]:stroke-2"
                  >
                    <rect
                      x={block.x}
                      y={block.y}
                      width={block.width}
                      height={block.height}
                      rx={6}
                      className="transition-[fill] duration-200 motion-reduce:transition-none"
                      // Dimmed stages fade towards the surface: no opacity, so the ribbons never show through.
                      style={{ fill: `color-mix(in oklab, ${BLOCK_FILL} ${lit(index) ? 100 : 35}%, var(--surface))` }}
                    />
                    <text
                      x={block.x + block.width / 2}
                      y={block.y + block.height / 2}
                      textAnchor="middle"
                      dominantBaseline="central"
                      className="pointer-events-none select-none font-bold tabular-nums"
                      style={{ fill: "var(--chart-on)", fontSize, letterSpacing: "-0.01em", fillOpacity: lit(index) ? 1 : 0.55 }}
                    >
                      {formatStageCount(stage)}
                    </text>
                  </g>
                </Tooltip>
              );
            })}
          </svg>
          {layout.blocks.map((block, index) => (
            <span
              key={`label-${stages[index].stage}`}
              aria-hidden
              className={
                vertical
                  ? "pointer-events-none absolute -translate-x-full -translate-y-1/2 truncate text-right text-xs font-semibold text-secondary"
                  : "pointer-events-none absolute line-clamp-2 -translate-x-1/2 -translate-y-full text-center text-xs font-semibold leading-4 text-secondary"
              }
              style={{
                left: block.labelX,
                top: block.labelY,
                width: vertical ? block.labelWidth : undefined,
                maxWidth: block.labelWidth,
                opacity: lit(index) ? 1 : 0.55,
                transition: "opacity 200ms",
              }}
            >
              {stages[index].label}
            </span>
          ))}
        </>
      )}
    </div>
  );
}
